import {
  IFragmentFragmentScreenDTO,
  IProteinFormulationIngredientsDTO,
  IProteinFormulationStandardIngredientsDTO,
  SeedInfoDTO,
} from 'business/models/Models';
import { ProteinFormulationData } from 'business/models/Layer';
import React, { useMemo, useState } from 'react';
import { FragmentInfo } from './FragmentInfo';
import {
  compareByNameWithNullsLast,
  getDateTime12HourFormat,
  getIngIdStockNumber,
} from 'utils/Global';
import { SeedInfo } from './SeedInfo';
import { FmlxCard, FmlxButton } from 'fmlx-common-ui';
import { ReactComponent as IngredientsSVG } from 'Icons/Sidebar/IngredientsRect.svg';
import {
  ChevronDownIcon,
  ChevronRightIcon,
  RockMakerProteinFormulationIcon,
} from '@fmlx/icons';
import FmlxBaseIcon from '@fmlx/icons/FmlxBaseIcon';
import { DropContentInfoCard } from './DropContentInfoCard';
import { ProteinDetailsCard } from './ProteinDetailsCard';
import { usePrecisionFormatter } from 'CustomHooks/usePrecisionFormatter';
import {
  EPrecisionType,
  PrecisionFormatter,
} from 'components/plateviewer/render/plate/plateviewer/constants';

export type { ProteinFormulationData };

export interface IIngredientProteinInfo {
  isScreenRoute: boolean;
  proteins: ProteinFormulationData[];
  seedInfos: SeedInfoDTO[];
  seedVolumeById?: Record<number, number>;
  well: any;
  fragmentIds?: IFragmentFragmentScreenDTO[];
  dropNumber?: number;
  containerClassName?: string;
}

const renderProteinIngredients = (
  ingredients: IProteinFormulationIngredientsDTO[],
  formatWithPrecision: PrecisionFormatter,
) => {
  if (!ingredients?.length) {
    return null;
  }

  return ingredients.map((protein, idx) => {
    const header = `${protein.name} - ${protein.typeName}`;
    const conc = [
      !isNaN(parseFloat(String(protein.concentrationmgml))) &&
        `${formatWithPrecision(
          parseFloat(String(protein.concentrationmgml)),
          EPrecisionType.Concentration,
        )} mg/mL`,
      !isNaN(parseFloat(String(protein.concentrationmM))) &&
        `${formatWithPrecision(
          parseFloat(String(protein.concentrationmM)),
          EPrecisionType.Concentration,
        )} mM`,
      !isNaN(parseFloat(String(protein.moleculerWeight))) &&
        `${parseFloat(String(protein.moleculerWeight)).toFixed(3)} kDa`,
    ]
      .filter(Boolean)
      .join(', ');
    return (
      <DropContentInfoCard
        key={idx}
        header={header}
        contents={[`Conc: ${conc}`]}
        icon={<RockMakerProteinFormulationIcon customSize={28} />}
      />
    );
  });
};

const renderStandardIngredients = (
  standardIngredients: IProteinFormulationStandardIngredientsDTO[],
  formatWithPrecision: PrecisionFormatter,
) => {
  if (!standardIngredients?.length) {
    return null;
  }
  return standardIngredients.map((standardIng, idx) => {
    const header = `${formatWithPrecision(
      parseFloat(String(standardIng.concentrationM)),
      EPrecisionType.Concentration,
    )} ${standardIng.stockConcentrationUnit} ${standardIng.ingredientName} - ${
      standardIng.type
    }`;
    const conc = `Stock ${standardIng.stockConcentration} ${standardIng.stockConcentrationUnit}`;
    const pH = standardIng.type === 'Buffer' ? `, pH ${standardIng.pH}` : ``;
    const stockNumber = ` ${getIngIdStockNumber(
      standardIng.ingredientID,
      standardIng.stockNumber,
    )}`;
    return (
      <DropContentInfoCard
        key={idx}
        header={header}
        contents={[`${conc}${pH}${stockNumber}`]}
        icon={
          <FmlxBaseIcon glyph={<IngredientsSVG />} size={'md'} className="mx-[2px]" />
        }
      />
    );
  });
};

const IngredientProteinInfo = (props: IIngredientProteinInfo) => {
  const {
    isScreenRoute,
    proteins,
    seedInfos,
    seedVolumeById = {},
    well,
    fragmentIds = [],
    dropNumber,
    containerClassName,
  } = props;

  const [expandedProteins, setExpandedProteins] = useState<Set<number>>(new Set());
  const formatWithPrecision = usePrecisionFormatter();

  const selectedDropNumber = dropNumber ?? proteins[0]?.dropNumber;

  const sortedProteins = useMemo(() => {
    return [...proteins].sort((a, b) => compareByNameWithNullsLast(a.name, b.name));
  }, [proteins]);

  if (!well || !selectedDropNumber) {
    return null;
  }

  const toggleProtein = (index: number) => {
    setExpandedProteins((prev) => {
      const next = new Set(prev);
      if (next.has(index)) {
        next.delete(index);
      } else {
        next.add(index);
      }
      return next;
    });
  };

  const getFragmentVolume = () => {
    const drop = well.dropList?.find((d) => d.dropNumber === selectedDropNumber);
    return drop?.dropFragmentVolume ?? 0;
  };

  return (
    <div
      id="ingredientproteininfo-main-container"
      className={containerClassName ?? 'h-[74vh] overflow-auto'}
    >
      {!isScreenRoute && (
        <>
          <div
            id="ingredientproteininfo-display"
            className="flex flex-col gap-3 overflow-hidden pt-0 p-3 bg-white overflow-y-auto overflow-x-hidden"
          >
            <div
              id="ingredientproteininfo-drop-label"
              className="bg-[#E0E0E0] rounded-md w-fit px-2 py-1"
            >
              <div className="font-medium text-sm">{`Drop ${selectedDropNumber}`}</div>
            </div>

            {sortedProteins.map((protein, i) => (
              <FmlxCard
                key={i}
                disabled={false}
                selected={false}
                selectable={false}
                draggable={false}
                variant="outlined"
                type={undefined}
              >
                <FmlxCard.Header className="flex flex-row items-center pb-0 gap-x-2 text-sm">
                  <FmlxButton
                    onClick={() => toggleProtein(i)}
                    onlyIcon
                    size={FmlxButton.Size.SMALL}
                    type={FmlxButton.Type.BASIC}
                    variant={FmlxButton.Variant.PLAIN}
                    icon={
                      expandedProteins.has(i) ? <ChevronDownIcon /> : <ChevronRightIcon />
                    }
                    withTooltip={false}
                    className="flex-shrink-0"
                  />
                  <div className="flex-1 truncate font-medium">{protein.name}</div>
                  {protein.datePrepared && (
                    <div className="flex-shrink-0 font-normal text-xs">
                      {getDateTime12HourFormat(protein.datePrepared)}
                    </div>
                  )}
                </FmlxCard.Header>
                <FmlxCard.Body className="!pt-0">
                  {expandedProteins.has(i) && (
                    <ProteinDetailsCard
                      protein={protein}
                      formatWithPrecision={formatWithPrecision}
                    />
                  )}

                  {protein.ingredients?.length > 0 && (
                    <div className="mt-2">
                      <div className="protein-ing-header">Protein Ingredients</div>
                      <div className="flex flex-col gap-2 mt-1">
                        {renderProteinIngredients(
                          protein.ingredients,
                          formatWithPrecision,
                        )}
                      </div>
                    </div>
                  )}

                  {protein.standardIngredients?.length > 0 && (
                    <div className="mt-2">
                      <div className="protein-ing-header">Standard Ingredients</div>
                      <div className="flex flex-col gap-2 mt-1">
                        {renderStandardIngredients(
                          protein.standardIngredients,
                          formatWithPrecision,
                        )}
                      </div>
                    </div>
                  )}
                </FmlxCard.Body>
              </FmlxCard>
            ))}
          </div>

          <div id="ingredientproteininfo-details-wrapper" className="px-3">
            <div
              id="ingredientproteininfo-details-fragment-title"
              className="font-bold text-sm font-[Roboto] mb-2"
            >
              Fragment
            </div>
            {fragmentIds?.length ? (
              <div id="ingredientproteininfo-details-fragment-info">
                <FragmentInfo
                  fragmentFragmentScreen={fragmentIds}
                  fragmentVolume={getFragmentVolume()}
                  formatWithPrecision={formatWithPrecision}
                />
              </div>
            ) : null}
            <div
              id="ingredientproteininfo-details-seed-title"
              className="font-bold text-sm font-[Roboto] my-2"
            >
              Seeds
            </div>
            {seedInfos?.length > 0 && (
              <div id="ingredientproteininfo-details-seed-info">
                <SeedInfo
                  seedInfos={seedInfos}
                  seedVolumeById={seedVolumeById}
                  formatWithPrecision={formatWithPrecision}
                />
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
};

export default IngredientProteinInfo;
